import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import YouTube from "react-youtube";

// Extraí o ID de um vídeo YouTube a partir de (quase) qualquer URL
function getYouTubeId(url) {
  const regExp =
    /^.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]{11}).*/;
  const match = url.match(regExp);
  return match ? match[1] : null;
}

/*
 * Player YouTube com métodos imperativos:
 *   – getCurrentTime()
 *   – seekTo(segundos)
 *   – getInternalPlayer()
 *   – togglePlay()
 */
const VIDEO_PROGRESS_KEY = "scoutVideoProgress";

const VideoPlayerWithControls = forwardRef(({ url }, ref) => {
  const ytRef = useRef(null); // referência ao player YouTube
  const [ready, setReady] = useState(false);
  // Ref para armazenar o estado de play/pause a ser restaurado

  // --- Salvamento automático da posição do vídeo ---
  useEffect(() => {
    if (!ytRef.current) return;
    let interval = setInterval(() => {
      // Só salva se estiver tocando ou pausado
      const state = ytRef.current.getPlayerState?.();
      const isPlaying = state === 1;
      const current = ytRef.current.getCurrentTime?.();
      if (typeof current === "number" && !isNaN(current)) {
        localStorage.setItem(
          VIDEO_PROGRESS_KEY,
          JSON.stringify({ url, time: current, isPlaying })
        );
      }
    }, 10000); // 10 segundos
    return () => clearInterval(interval);
  }, [url, ready]);

  // --- Restaurar posição e estado ao montar ---
  useEffect(() => {
    const saved = localStorage.getItem(VIDEO_PROGRESS_KEY);
    if (saved) {
      try {
        const { url: savedUrl, time, isPlaying } = JSON.parse(saved);
        if (savedUrl === url && typeof time === "number") {
          // Espera o player estar pronto
          setTimeout(() => {
            ytRef.current?.seekTo?.(time, true);
          }, 500);
        } else if (savedUrl !== url) {
          // Novo vídeo: sobrescreve o progresso
          localStorage.setItem(
            VIDEO_PROGRESS_KEY,
            JSON.stringify({ url, time: 0, isPlaying: false })
          );
        }
      } catch {}
    }
  }, [url, ready]); // Sem dependência 'ready' aqui, pois usaremos onReady para acionar

  /* ----------- Métodos expostos ao componente pai ----------- */
  useImperativeHandle(ref, () => ({
    getCurrentTime: () => ytRef.current?.getCurrentTime() ?? 0,
    seekTo: (s) => ytRef.current?.seekTo(s, true),
    getInternalPlayer: () => ytRef.current ?? null,
    togglePlay: () => {
      if (!ytRef.current) return;
      const state = ytRef.current.getPlayerState();
      // 1 = playing, 2 = paused, 0 = ended, -1 = unstarted
      if (state === 1) {
        ytRef.current.pauseVideo();
      } else {
        ytRef.current.playVideo();
      }
    },
  }));

  /* ----------- Handler YouTube onReady ----------- */
  const onReady = (e) => {
    ytRef.current = e.target; // guarda instância do player
    setReady(true);
  };

  const videoId = getYouTubeId(url);
  if (!videoId) return <div>URL de vídeo do YouTube inválida.</div>;

  return (
    <div className="w-full aspect-video lg:min-w-[950px] lg:max-w-[950px] mx-auto">
      <YouTube
        videoId={videoId}
        onReady={onReady}
        className="w-full h-full"
        opts={{
          width: "100%",
          height: "100%",
          playerVars: { controls: 1 },
        }}
      />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500"></div>
      )}
    </div>
  );
});

export default VideoPlayerWithControls;
